import { faBuildingColumns, faVault, faSearch, faCashRegister } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { getCashbox, getCashboxes } from '@redux/features/cashboxesSlice';
import { loadMoreTransactions } from '@redux/features/transactionsSlice';
import { addNotification } from '@redux/features/notificationsSlice';
import { getUkrainianPlural } from 'helpers/getUkrainianPlural';
import { getEntityLink } from 'helpers/getEntityLink';
import { CASHBOX_TYPES, CASHBOX_TYPES_UA } from 'constants/cashboxTypes';
import { MoneyAmount } from 'components/MoneyAmount';
import { BackButton } from 'components/BackButton';
import { Button } from 'components/Button';
import { CreateTransferForm } from '../forms/CreateTransferForm';
import { EditAccountForm } from '../forms/EditAccountForm';
import { CreateTransaction } from './CreateTransaction';
import { TransactionCard } from './TransactionCard';
import { Actions } from '../Actions';
import { Filters } from './Filters';
import styles from './CashboxPage.scss';

export const CashboxPage = () => {
  const { transactions, isLoading, isLoadingMore, error, meta, currentParams } = useSelector(
    (state) => state.transactions
  );
  const [isCreateTransactionModalOpen, setIsCreateTransactionModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const { cashboxes } = useSelector((state) => state.cashboxes);
  const [editingCashbox, setEditingCashbox] = useState(null);
  const { cashboxID } = useParams();
  const sentinelRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!cashboxes.map((c) => c.id).includes(Number(cashboxID))) {
      dispatch(getCashbox(cashboxID));
    }
  }, [cashboxID, cashboxes, dispatch]);

  const cashbox = useMemo(() => cashboxes.find((c) => c.id === Number(cashboxID)), [cashboxes, cashboxID]);

  const loadMore = useCallback(() => {
    if (!isLoadingMore && meta.hasMore && currentParams) {
      const nextPageParams = {
        ...currentParams,
        page: meta.page + 1,
      };
      dispatch(loadMoreTransactions(nextPageParams));
    }
  }, [isLoadingMore, meta.hasMore, meta.page, currentParams, dispatch]);

  useInfiniteScroll({
    sentinelRef,
    hasMore: meta.hasMore,
    isLoading: isLoadingMore,
    onLoadMore: loadMore,
  });

  const handleCreateTransfer = async () => {
    const resultAction = await dispatch(getCashboxes());

    if (getCashboxes.fulfilled.match(resultAction)) {
      setIsTransferModalOpen(true);
    } else {
      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Помилка створення переказу',
          type: 'error',
        })
      );
    }
  };

  const getCashboxIcon = (type) => {
    if (type === CASHBOX_TYPES.ACCOUNT) return faBuildingColumns;
    if (type === CASHBOX_TYPES.SAFE) return faVault;
    if (type === CASHBOX_TYPES.REGISTER) return faCashRegister;
  };

  const getCashboxDisplayName = (type) => {
    if (type === CASHBOX_TYPES.SAFE) return `Сейф ${cashbox?.location?.name}`;
    if (type === CASHBOX_TYPES.REGISTER) return `Каса ${cashbox?.location?.name}`;
    return cashbox?.name;
  };

  return (
    <div className={styles.page}>
      <BackButton text='Рахунки' path={getEntityLink('finance')} />

      <div className={styles.layout}>
        <div className={styles.leftPanel}>
          <div className={styles.cashboxHeader}>
            <div className={styles.cashboxHeaderRow}>
              <div className={styles.cashboxIcon}>
                <FontAwesomeIcon icon={getCashboxIcon(cashbox?.type)} />
              </div>
              <div className={styles.cashboxInfo}>
                <h1 className={styles.cashboxName}>{getCashboxDisplayName(cashbox?.type)}</h1>
                <p className={styles.cashboxSubtitle}>{CASHBOX_TYPES_UA[cashbox?.type]}</p>
              </div>
              {cashbox?.type === CASHBOX_TYPES.ACCOUNT && (
                <Actions cashbox={cashbox} setEditingCashbox={setEditingCashbox} />
              )}
            </div>
            <div className={styles.additionalInfo}>
              {!!cashbox?.methods.length && (
                <div className={styles.additionalInfoColumn}>
                  <span className={styles.methodsTitle}>Коротка назва:</span>{' '}
                  <p className={styles.methods}>{cashbox?.methods.find((m) => m.type === 'basic')?.label}</p>
                </div>
              )}

              {cashbox?.methods.some((m) => m.type === 'additional') && (
                <div className={styles.additionalInfoColumn}>
                  <span className={styles.methodsTitle}>Способи поповнення:</span>{' '}
                  <p className={styles.methods}>
                    {cashbox?.methods
                      .filter((m) => m.type === 'additional')
                      .map((m) => m.label)
                      .join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className={styles.balanceCard}>
            <span className={styles.balanceLabel}>Поточний баланс</span>
            <MoneyAmount value={cashbox?.balance || 0} className={styles.balanceAmount} />
          </div>

          <div className={styles.actionButtons}>
            <Button
              text='Нова транзакція'
              className={styles.btn}
              onClick={() => setIsCreateTransactionModalOpen(true)}
            />
            <Button text='Переказ' className={styles.btn} onClick={handleCreateTransfer} ghostStyle />
          </div>

          <Filters
            cashbox={cashbox}
            hasActiveFilters={hasActiveFilters}
            setHasActiveFilters={setHasActiveFilters}
            transactionsLength={meta.total}
          />
        </div>

        <div className={styles.rightPanel}>
          <div className={styles.transactionsHeader}>
            <h2 className={styles.sectionTitle}>Транзакції</h2>
            {meta.total > 0 && (
              <span className={styles.totalCount}>
                Знайдено {meta.total} {getUkrainianPlural(meta.total, ['запис', 'записи', 'записів'])}
              </span>
            )}
          </div>

          <div className={styles.transactionsContent}>
            {isLoading && transactions.length === 0 && <p className={styles.loader}>Завантаження транзакцій...</p>}

            {error && (
              <div className={styles.error}>
                <p>Помилка завантаження транзакцій: {error}</p>
              </div>
            )}

            {!isLoading && !error && transactions.length === 0 && !hasActiveFilters && (
              <div className={styles.empty}>
                <FontAwesomeIcon icon={faSearch} className={styles.emptyIcon} />
                <h4>Транзакцій не знайдено</h4>
                <p>На цьому рахунку історія транзакцій відсутня</p>
                <Button onClick={() => setIsCreateTransactionModalOpen(true)} className={styles.createBtn} smallStyle>
                  Створити першу транзакцію
                </Button>
              </div>
            )}

            {!isLoading && !error && transactions.length === 0 && hasActiveFilters && (
              <div className={styles.empty}>
                <FontAwesomeIcon icon={faSearch} className={styles.emptyIcon} />
                <h4>За вашими фільтрами нічого не знайдено</h4>
                <p>Спробуйте змінити критерії пошуку</p>
              </div>
            )}

            {!isLoading && !error && transactions.length > 0 && (
              <div className={styles.transactionsList}>
                {transactions.map((t) => (
                  <TransactionCard transaction={t} key={t.id} />
                ))}

                <div ref={sentinelRef} className={styles.sentinel}>
                  {isLoadingMore && <p className={styles.loadMore}>Завантажуємо ще...</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <EditAccountForm cashbox={editingCashbox} isOpen={editingCashbox} setIsOpen={setEditingCashbox} />
      <CreateTransferForm
        cashboxes={cashboxes}
        isOpen={isTransferModalOpen}
        setIsOpen={setIsTransferModalOpen}
        initialFromCashbox={cashbox}
        hideFromField
      />
      <CreateTransaction
        cashboxID={cashbox?.id}
        isOpen={isCreateTransactionModalOpen}
        setIsOpen={setIsCreateTransactionModalOpen}
      />
    </div>
  );
};
